/**
 * socket.io client (path /api/v2/socket.io, cookie auth, auto-reconnect).
 *
 *  - table rooms: `subscribe(db, table)` is ref-counted; the server join happens on the first
 *    subscriber and is repeated after every reconnect; `unsubscribe` on the last one
 *  - record rooms (`record:subscribe`) likewise, for open forms (3e)
 *  - event bus for `record:created|updated|deleted` and `lock:changed` (payloads carry ids only;
 *    consumers re-fetch through the API, so rights apply to live updates as well) and the
 *    background job events `job:queued|started|progress|done` of the user's own room
 *  - `auth:expired` (or a handshake rejected as unauthenticated) → `onAuthExpired` (logout)
 *  - socket.io-client is imported lazily: it is not part of the initial app shell chunk
 */
import { reactive } from 'vue';
import type { ManagerOptions, Socket, SocketOptions } from 'socket.io-client';

export const SOCKET_PATH = '/api/v2/socket.io';

export interface RecordEvent {
  db: string;
  table: string;
  id: string;
  by?: string;
  at?: string;
}
export interface LockEvent {
  db: string;
  table: string;
  id: string;
  locked: boolean;
  user?: string;
  expiresAt?: string;
  by?: string;
}
export interface LockInfo {
  id?: string;
  user: string;
  since?: string;
  expiresAt?: string;
}
/** Background job events (room u:<user>, Phase 4d); payload as in functions/types.ts JobEventPayload. */
export interface JobEvent {
  id: string;
  function: string;
  label?: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress?: { executed: number; total: number; line: number; loops: string[]; percent: number };
  success?: boolean;
  error?: { status: number; code: string; message: string; details?: Record<string, unknown> };
}
export interface RealtimeEvents {
  'record:created': RecordEvent;
  'record:updated': RecordEvent;
  'record:deleted': RecordEvent;
  'lock:changed': LockEvent;
  'job:queued': JobEvent;
  'job:started': JobEvent;
  'job:progress': JobEvent;
  'job:done': JobEvent;
}
export type RealtimeEventName = keyof RealtimeEvents;
type Handler<E extends RealtimeEventName> = (payload: RealtimeEvents[E]) => void;

export interface Ack {
  ok: boolean;
  error?: string;
  message?: string;
  locks?: LockInfo[];
  lock?: LockInfo;
  [k: string]: unknown;
}

export type IoFactory = (opts: Partial<ManagerOptions & SocketOptions>) => Socket | Promise<Socket>;

const defaultFactory: IoFactory = async (opts) => (await import('socket.io-client')).io(opts);

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting';

export class RealtimeService {
  readonly status = reactive<{ state: ConnectionState; lastError: string | null }>({ state: 'idle', lastError: null });
  onAuthExpired: () => void = () => {};
  private socket: Socket | undefined;
  private connecting: Promise<Socket> | undefined;
  private tables = new Map<string, number>();
  private records = new Map<string, number>();
  private listeners = new Map<RealtimeEventName, Set<Handler<RealtimeEventName>>>();
  /** locks reported by the last subscribe ack per table (key db.table) */
  readonly locks = reactive(new Map<string, Map<string, LockInfo>>());

  constructor(
    private factory: IoFactory = defaultFactory,
    private ackTimeoutMs = 10_000,
  ) {}

  get connected(): boolean {
    return this.status.state === 'connected';
  }

  /** Open the connection (idempotent). */
  connect(): Promise<Socket> {
    if (this.socket) return Promise.resolve(this.socket);
    if (this.connecting) return this.connecting;
    this.status.state = 'connecting';
    this.connecting = Promise.resolve(
      this.factory({ path: SOCKET_PATH, withCredentials: true, reconnection: true, reconnectionDelay: 1000, reconnectionDelayMax: 15_000 }),
    ).then((s) => {
      this.socket = s;
      this.connecting = undefined;
      this.wire(s);
      return s;
    });
    return this.connecting;
  }

  /** Close the connection and forget all subscriptions (logout). */
  disconnect(): void {
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = undefined;
    this.connecting = undefined;
    this.tables.clear();
    this.records.clear();
    this.locks.clear();
    this.status.state = 'idle';
  }

  private wire(s: Socket): void {
    s.on('connect', () => {
      this.status.state = 'connected';
      this.status.lastError = null;
      // (re)join every room that still has subscribers
      for (const key of this.tables.keys()) void this.joinTable(key);
      for (const key of this.records.keys()) void this.joinRecord(key);
    });
    s.on('disconnect', (reason: string) => {
      // "io client disconnect" = our own disconnect(); everything else reconnects automatically
      this.status.state = reason === 'io client disconnect' ? 'idle' : 'reconnecting';
      // the server ends sockets of dead sessions with "io server disconnect" (no auto-reconnect)
      if (reason === 'io server disconnect' && this.socket === s) s.connect();
    });
    s.on('connect_error', (err: Error) => {
      this.status.lastError = err.message;
      this.status.state = 'reconnecting';
      if (err.message === 'unauthenticated' || err.message === 'invalid_token') this.expire();
    });
    s.on('auth:expired', () => this.expire());
    for (const ev of ['record:created', 'record:updated', 'record:deleted', 'lock:changed', 'job:queued', 'job:started', 'job:progress', 'job:done'] as const) {
      s.on(ev, (payload: RealtimeEvents[typeof ev]) => this.dispatch(ev, payload));
    }
  }

  private expire(): void {
    this.disconnect();
    this.onAuthExpired();
  }

  private dispatch<E extends RealtimeEventName>(event: E, payload: RealtimeEvents[E]): void {
    if (event === 'lock:changed') {
      const p = payload as LockEvent;
      const key = `${p.db}.${p.table}`;
      const m = this.locks.get(key);
      if (m) {
        if (p.locked && p.user) m.set(p.id, { id: p.id, user: p.user, ...(p.expiresAt ? { expiresAt: p.expiresAt } : {}) });
        else m.delete(p.id);
      }
    }
    for (const h of this.listeners.get(event) ?? []) {
      try {
        (h as Handler<E>)(payload);
      } catch (e) {
        console.error('realtime listener failed', e);
      }
    }
  }

  /** Listen to an event; returns the unsubscribe function. */
  on<E extends RealtimeEventName>(event: E, handler: Handler<E>): () => void {
    const set = this.listeners.get(event) ?? new Set();
    set.add(handler as Handler<RealtimeEventName>);
    this.listeners.set(event, set);
    return () => set.delete(handler as Handler<RealtimeEventName>);
  }

  /** Emit with acknowledgement (rejects on timeout). */
  async request(event: string, payload: Record<string, unknown>): Promise<Ack> {
    const s = await this.connect();
    return (await s.timeout(this.ackTimeoutMs).emitWithAck(event, payload)) as Ack;
  }

  private async joinTable(key: string): Promise<void> {
    const [db, table] = splitKey(key);
    if (!this.socket?.connected) return; // joined on connect
    try {
      const ack = await this.request('subscribe', { db, table });
      if (ack.ok) this.locks.set(key, new Map((ack.locks ?? []).map((l) => [String(l.id), l])));
      else console.warn(`realtime: subscribe ${key} refused`, ack.error);
    } catch (e) {
      console.warn(`realtime: subscribe ${key} failed`, e);
    }
  }

  private async joinRecord(key: string): Promise<void> {
    const [db, table, id] = splitKey(key);
    if (!this.socket?.connected) return;
    try {
      await this.request('record:subscribe', { db, table, id });
    } catch (e) {
      console.warn(`realtime: record:subscribe ${key} failed`, e);
    }
  }

  /** Subscribe to a table room (ref-counted); returns the unsubscribe function. */
  subscribe(db: string, table: string): () => void {
    const key = `${db}.${table}`;
    const n = this.tables.get(key) ?? 0;
    this.tables.set(key, n + 1);
    if (n === 0) void this.connect().then(() => this.joinTable(key));
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const left = (this.tables.get(key) ?? 1) - 1;
      if (left > 0) return void this.tables.set(key, left);
      this.tables.delete(key);
      this.locks.delete(key);
      if (this.socket?.connected) void this.request('unsubscribe', { db, table }).catch(() => {});
    };
  }

  /** Subscribe to a record room (ref-counted); returns the unsubscribe function. */
  subscribeRecord(db: string, table: string, id: string): () => void {
    const key = `${db}.${table}.${id}`;
    const n = this.records.get(key) ?? 0;
    this.records.set(key, n + 1);
    if (n === 0) void this.connect().then(() => this.joinRecord(key));
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const left = (this.records.get(key) ?? 1) - 1;
      if (left > 0) return void this.records.set(key, left);
      this.records.delete(key);
      if (this.socket?.connected) void this.request('record:unsubscribe', { db, table, id }).catch(() => {});
    };
  }

  /** Current subscriber counts (diagnostics / tests). */
  subscriptions(): { tables: Record<string, number>; records: Record<string, number> } {
    return { tables: Object.fromEntries(this.tables), records: Object.fromEntries(this.records) };
  }
}

/** `db.table[.id]` – db and table are identifiers without dots; the id may contain dots. */
function splitKey(key: string): [string, string, string] {
  const i = key.indexOf('.');
  const j = key.indexOf('.', i + 1);
  return j < 0 ? [key.slice(0, i), key.slice(i + 1), ''] : [key.slice(0, i), key.slice(i + 1, j), key.slice(j + 1)];
}

export const realtime = new RealtimeService();
